import * as THREE from 'three';
import { TRACK_CENTERLINE, TRACK_HALF_WIDTH, JUMP_ZS } from '../utils/constants.js';
import { Racer } from './Racer.js';

// Scene owns the THREE.Scene, lighting, terrain, and decoration. The terrain
// is a heightmap-displaced strip that follows the track centerline — smooth
// near the racing line, bumpy further out — so the slope reads as an actual
// mountain rather than a flat plane.
export class Scene {
  constructor() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x9ec9ff);
    this.scene.fog = new THREE.Fog(0x9ec9ff, 240, 720);

    // worldGroup holds every track-dependent mesh (terrain, gates, fences,
    // banners, jumps, trees, rocks, podium boxes, etc.). Swapping tracks
    // removes this group and rebuilds, leaving the lighting in place.
    this.worldGroup = new THREE.Group();
    this.scene.add(this.worldGroup);

    // Obstacles the GameLoop needs to know about (course-side trees the
    // player can collide with). Populated by _buildObstacleTrees().
    this.obstacles = [];

    // Jump pads the GameLoop checks for launch behavior. Each entry is
    // { x, z, yaw, halfLength, halfWidth }. Populated by _buildJumps().
    this.jumpPads = [];

    this._setupLighting();
    this._buildWorld();
  }

  // Builds everything that depends on the current TRACK_CENTERLINE. Called
  // from the constructor and from rebuildWorld() on track change.
  _buildWorld() {
    this._buildTerrain();
    this._buildGroomingLines();
    this._buildStartArea();
    this._buildFinishArea();
    this._buildSideFences();
    this._buildSponsorBanners();
    this._buildJumps();
    this._buildDecoration();
    this._buildObstacleTrees();
  }

  // Clears the current track meshes and rebuilds from the (now-updated)
  // TRACK_CENTERLINE. Lighting and any post-race podium are untouched.
  // obstacles + jumpPads are cleared *in place* so the references GameLoop
  // captured at startup stay valid after a track change.
  rebuildWorld() {
    this.scene.remove(this.worldGroup);
    this.worldGroup = new THREE.Group();
    this.scene.add(this.worldGroup);
    this.obstacles.length = 0;
    this.jumpPads.length = 0;
    this._buildWorld();
  }

  // Course-side obstacle trees: planted near the inside of the fence corridor
  // so the player can hit them if they wander too wide. Their world positions
  // are stored in this.obstacles for collision detection.
  _buildObstacleTrees() {
    const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x4d2e15, roughness: 0.95 });
    const snowMaterial = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.85 });
    const trunkGeo = new THREE.CylinderGeometry(0.22, 0.32, 4.0, 7);
    const branchGeo = new THREE.CylinderGeometry(0.06, 0.11, 1.1, 5);
    const capGeo = new THREE.SphereGeometry(0.7, 9, 7);
    const capTopGeo = new THREE.SphereGeometry(0.45, 9, 7);

    const rng = mulberry32(0xdeadbeef);
    // Spread along Z evenly; alternate side; lateral 4-7 from centerline
    // so they sit just inside the fence corridor (fence at TRACK_HALF_WIDTH+2).
    const segments = TRACK_CENTERLINE.length - 1;
    const treesPerSegment = 1.4; // ~17 trees total over the course
    const total = Math.round(segments * treesPerSegment);

    for (let i = 0; i < total; i++) {
      const segIdx = Math.floor((i / total) * segments);
      const segT = ((i / total) * segments) - segIdx;
      const a = TRACK_CENTERLINE[segIdx];
      const b = TRACK_CENTERLINE[segIdx + 1];
      const cx = a.x + (b.x - a.x) * segT;
      const cz = a.z + (b.z - a.z) * segT;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const segLen = Math.hypot(dx, dz);
      const rx = -dz / segLen;
      const rz = dx / segLen;

      const side = (i % 2 === 0) ? -1 : 1;
      // Inside the fence (at TRACK_HALF_WIDTH + 2). Place 4-7 from centerline.
      const lateral = 4 + rng() * 3;
      const x = cx + rx * lateral * side;
      const z = cz + rz * lateral * side;
      const y = terrainY(x, z);

      const tree = new THREE.Group();

      const trunk = new THREE.Mesh(trunkGeo, trunkMaterial);
      trunk.position.y = 2.0;
      trunk.castShadow = true;
      trunk.receiveShadow = true;
      tree.add(trunk);

      // A few branches.
      const branchCount = 4 + Math.floor(rng() * 3);
      for (let bi = 0; bi < branchCount; bi++) {
        const branch = new THREE.Mesh(branchGeo, trunkMaterial);
        const angle = rng() * Math.PI * 2;
        const tilt = 0.5 + rng() * 0.6;
        branch.position.set(Math.cos(angle) * 0.28, 2.8 + rng() * 0.7, Math.sin(angle) * 0.28);
        branch.rotation.set(tilt, angle, 0);
        branch.castShadow = true;
        tree.add(branch);
      }

      // Snow cap.
      const cap = new THREE.Mesh(capGeo, snowMaterial);
      cap.position.y = 4.0;
      cap.scale.y = 0.6;
      tree.add(cap);
      const capTop = new THREE.Mesh(capTopGeo, snowMaterial);
      capTop.position.y = 4.4;
      capTop.scale.y = 0.5;
      tree.add(capTop);

      tree.position.set(x, y, z);
      const scale = 0.95 + rng() * 0.5;
      tree.scale.setScalar(scale);
      this.worldGroup.add(tree);

      // Collider: cylinder centred at trunk base, radius scales with tree.
      this.obstacles.push({
        x, z,
        radius: 0.5 * scale,
      });
    }
  }

  // GameLoop uses this to read obstacle collider positions.
  getObstacles() {
    return this.obstacles;
  }

  _setupLighting() {
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    this.scene.add(new THREE.HemisphereLight(0xbfdfff, 0xffffff, 0.45));

    const sun = new THREE.DirectionalLight(0xffffff, 1.15);
    sun.position.set(80, 220, 80);
    sun.castShadow = true;
    sun.shadow.camera.left = -240;
    sun.shadow.camera.right = 240;
    sun.shadow.camera.top = 240;
    sun.shadow.camera.bottom = -240;
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 700;
    sun.shadow.mapSize.set(2048, 2048);
    this.scene.add(sun);
  }

  // Builds a terrain mesh whose Y is dominated by the centerline (so the
  // racing line is smooth) plus a noise field that ramps in as you move
  // laterally away from the path (so the surroundings look like mountains).
  _buildTerrain() {
    const widthSegments = 80;
    const lengthSegments = 120;
    const halfWidth = 140;        // x ∈ [-140, +140]
    const startZ = TRACK_CENTERLINE[0].z + 40;
    const endZ = TRACK_CENTERLINE[TRACK_CENTERLINE.length - 1].z - 60;
    const length = startZ - endZ;
    const width = halfWidth * 2;

    const geo = new THREE.PlaneGeometry(width, length, widthSegments, lengthSegments);
    // Rotate so the plane lies on XZ with -Z going downhill.
    geo.rotateX(-Math.PI / 2);
    // The PlaneGeometry was constructed centered at origin in XY; after rotate,
    // its X stays X, but what was Y is now -Z. Translate so the plane spans
    // [startZ..endZ] along Z.
    geo.translate(0, 0, (startZ + endZ) / 2);

    const trackHalfWidth = TRACK_HALF_WIDTH;
    const transitionWidth = 24;

    const pos = geo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      // Terrain height comes from the same terrainY() helper used by every
      // on-ground placement below — keeps decorations from floating.
      pos.setY(i, terrainY(x, z));
    }
    geo.computeVertexNormals();

    // Per-vertex color so the racing line reads as bright snow while the
    // mountainsides shade darker — like the cartoon mountains in
    // Snowboard Kids that go from white snowcaps to dark slate flanks.
    const colors = new Float32Array(pos.count * 3);
    const snow = new THREE.Color(0xffffff);
    const flank = new THREE.Color(0x6b7e94);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const [centerX] = sampleCenterline(z);
      const lateral = Math.abs(x - centerX);
      const t = clamp((lateral - trackHalfWidth - 6) / (transitionWidth * 0.9), 0, 1);
      const c = snow.clone().lerp(flank, t * 0.85);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.95,
      metalness: 0.0,
      flatShading: true,
    });
    const mesh = new THREE.Mesh(geo, material);
    mesh.receiveShadow = true;
    this.worldGroup.add(mesh);

    // Cache for decoration placement.
    this._terrainBounds = { halfWidth, startZ, endZ, trackHalfWidth, transitionWidth };
  }

  // Start area: a flat staging platform behind the start gate with side
  // banners flanking the corridor, so the player sees they're "in the gate".
  _buildStartArea() {
    const start = TRACK_CENTERLINE[0];

    // Staging platform: a 24x24 box rising to the start point's height.
    const platMat = new THREE.MeshStandardMaterial({ color: 0xf1f5f9, roughness: 0.7 });
    const plat = new THREE.Mesh(new THREE.BoxGeometry(40, 1.2, 24), platMat);
    plat.position.set(start.x, start.y - 0.6, start.z + 12);
    plat.receiveShadow = true;
    plat.castShadow = true;
    this.worldGroup.add(plat);

    // Start gate.
    this.worldGroup.add(buildGate(start.x, start.y, start.z, 0x3b82f6, 'START'));

    // Side banners running back into the staging area.
    for (const side of [-1, 1]) {
      const banner = new THREE.Mesh(
        new THREE.BoxGeometry(0.4, 2.4, 22),
        new THREE.MeshStandardMaterial({ color: 0x1e3a8a }),
      );
      banner.position.set(start.x + side * 9, start.y + 1, start.z + 11);
      banner.castShadow = true;
      this.worldGroup.add(banner);
    }

    // Sponsor flags on top of the staging platform.
    for (let i = 0; i < 6; i++) {
      const flag = makeFlag(i % 2 ? 0xfbbf24 : 0xffffff);
      const xOff = -16 + (i * 6.4);
      flag.position.set(start.x + xOff, start.y, start.z + 20);
      this.worldGroup.add(flag);
    }
  }

  // Finish area: a wider run-out past the finish line with a podium and
  // multiple banner posts so it reads as a proper race end.
  _buildFinishArea() {
    const finish = TRACK_CENTERLINE[TRACK_CENTERLINE.length - 1];

    // Run-out: a flat slab continuing past the finish.
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(80, 1.0, 60),
      new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.7 }),
    );
    slab.position.set(finish.x, finish.y - 0.5, finish.z - 30);
    slab.receiveShadow = true;
    this.worldGroup.add(slab);

    // Finish gate.
    this.worldGroup.add(buildGate(finish.x, finish.y, finish.z, 0xef4444, 'FINISH'));

    // Podium: three boxes of descending height. Tops are recorded so the
    // race-over flow can place winners on them.
    const podiumZ = finish.z - 36;
    this._podiumTops = []; // [{place, x, y, z}] for 1st/2nd/3rd in that order
    for (const [place, x, h, color] of [
      [1, 0, 4.5, 0xfbbf24],
      [2, -5, 3.0, 0xcbd5e1],
      [3, 5, 2.0, 0xb45309],
    ]) {
      const box = new THREE.Mesh(
        new THREE.BoxGeometry(4, h, 4),
        new THREE.MeshStandardMaterial({ color }),
      );
      box.position.set(finish.x + x, finish.y + h / 2, podiumZ);
      box.castShadow = true;
      box.receiveShadow = true;
      this.worldGroup.add(box);
      this._podiumTops.push({ place, x: finish.x + x, y: finish.y + h, z: podiumZ });
    }
    this._podiumFocus = { x: finish.x, y: finish.y + 3, z: podiumZ };

    // Banner posts lining the run-out.
    for (let i = 0; i < 4; i++) {
      const z = finish.z - 8 - i * 12;
      for (const side of [-1, 1]) {
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.2, 0.2, 5, 8),
          new THREE.MeshStandardMaterial({ color: 0xf1f5f9 }),
        );
        post.position.set(finish.x + side * 18, finish.y + 2.5, z);
        post.castShadow = true;
        this.worldGroup.add(post);

        const flag = makeFlag(side > 0 ? 0xef4444 : 0xffffff);
        flag.position.set(finish.x + side * 18, finish.y, z);
        this.worldGroup.add(flag);
      }
    }
  }

  // Wooden post-and-rail fencing in the Snowboard Kids style: thin posts at
  // regular intervals connecting a low horizontal rail. The fence runs along
  // both sides of the racing corridor and lines up with the wall-collision
  // bound (MAX_LATERAL ≈ TRACK_HALF_WIDTH + 1.5).
  _buildSideFences() {
    const fenceOffset = TRACK_HALF_WIDTH + 2;
    const postHeight = 1.2;
    const railHeight = 0.95;
    const railThickness = 0.18;
    const postRadius = 0.14;
    const postSpacing = 4; // metres between posts

    const woodMat = new THREE.MeshStandardMaterial({ color: 0x8a5a3b, roughness: 0.85 });
    const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x654321, roughness: 0.85 });

    for (let i = 0; i < TRACK_CENTERLINE.length - 1; i++) {
      const a = TRACK_CENTERLINE[i];
      const b = TRACK_CENTERLINE[i + 1];
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const length = Math.hypot(dx, dz);
      const tangent = { x: dx / length, z: dz / length };
      const right = { x: -tangent.z, z: tangent.x };
      // Match the racer's yaw convention: yaw 0 means facing -Z (downhill).
      const yaw = Math.atan2(tangent.x, -tangent.z);

      const segments = Math.max(1, Math.round(length / postSpacing));
      const subLen = length / segments;

      for (let s = 0; s < segments; s++) {
        const t = (s + 0.5) / segments;
        const cx = a.x + dx * t;
        const cy = a.y + (b.y - a.y) * t;
        const cz = a.z + dz * t;

        for (const side of [-1, 1]) {
          const px = cx + right.x * fenceOffset * side;
          const pz = cz + right.z * fenceOffset * side;
          const groundY = terrainY(px, pz);

          // Horizontal rail (one per sub-segment).
          const rail = new THREE.Mesh(
            new THREE.BoxGeometry(railThickness, railThickness, subLen),
            woodMat,
          );
          rail.position.set(px, groundY + railHeight, pz);
          rail.rotation.y = yaw;
          rail.castShadow = true;
          rail.receiveShadow = true;
          this.worldGroup.add(rail);

          // Post at the leading end of this sub-segment.
          const post = new THREE.Mesh(
            new THREE.CylinderGeometry(postRadius, postRadius, postHeight, 6),
            darkWoodMat,
          );
          const pt = s / segments;
          const postX = (a.x + dx * pt) + right.x * fenceOffset * side;
          const postZ = (a.z + dz * pt) + right.z * fenceOffset * side;
          post.position.set(postX, terrainY(postX, postZ) + postHeight / 2, postZ);
          post.castShadow = true;
          post.receiveShadow = true;
          this.worldGroup.add(post);
        }
      }
    }
  }

  // Sponsor banners: yellow/red banners hung between pairs of tall posts at a
  // few spots along the course. Sits outside the fence so it doesn't block
  // the racing line.
  _buildSponsorBanners() {
    const bannerSpots = [-100, -250, -380, -540, -680];
    const bannerOffset = TRACK_HALF_WIDTH + 6;
    const bannerHeight = 3.5;
    const bannerLen = 14;
    const colors = [0xfbbf24, 0xef4444, 0xfbbf24, 0xef4444, 0xfbbf24];
    const postMat = new THREE.MeshStandardMaterial({ color: 0x654321, roughness: 0.85 });

    for (let i = 0; i < bannerSpots.length; i++) {
      const z = bannerSpots[i];
      const [cx] = sampleCenterline(z);
      const tangent = tangentAt(z);
      const right = { x: -tangent.z, z: tangent.x };
      // Match the racer's yaw convention: yaw 0 means facing -Z (downhill).
      const yaw = Math.atan2(tangent.x, -tangent.z);
      const side = i % 2 === 0 ? 1 : -1;
      const bx = cx + right.x * bannerOffset * side;
      const bz = z + right.z * bannerOffset * side;
      const groundY = terrainY(bx, bz);

      // Two posts at either end of the banner length.
      for (const end of [-1, 1]) {
        const postX = bx + tangent.x * (bannerLen / 2) * end;
        const postZ = bz + tangent.z * (bannerLen / 2) * end;
        const postH = bannerHeight + 1.5;
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.18, 0.18, postH, 6),
          postMat,
        );
        post.position.set(postX, terrainY(postX, postZ) + postH / 2, postZ);
        post.castShadow = true;
        this.worldGroup.add(post);
      }

      // Banner stretched between the posts at near-top height.
      const banner = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 1.4, bannerLen),
        new THREE.MeshStandardMaterial({ color: colors[i % colors.length], roughness: 0.85 }),
      );
      banner.position.set(bx, groundY + bannerHeight - 0.5, bz);
      banner.rotation.y = yaw;
      banner.castShadow = true;
      banner.receiveShadow = true;
      this.worldGroup.add(banner);
    }
  }

  // Snow grooming lines: thin darker stripes along the racing line, like the
  // parallel grooves left by a piste basher. Pure decoration but gives the
  // course an obvious "this is a groomed run" look.
  _buildGroomingLines() {
    const grooveCount = 5; // total grooves across the racing surface
    const grooveMat = new THREE.MeshStandardMaterial({ color: 0xdce4ec, roughness: 0.9 });

    for (let i = 0; i < TRACK_CENTERLINE.length - 1; i++) {
      const a = TRACK_CENTERLINE[i];
      const b = TRACK_CENTERLINE[i + 1];
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const length = Math.hypot(dx, dz);
      const tangent = { x: dx / length, z: dz / length };
      const right = { x: -tangent.z, z: tangent.x };
      // Match the racer's yaw convention: yaw 0 means facing -Z (downhill).
      const yaw = Math.atan2(tangent.x, -tangent.z);

      // Slope pitch of this segment (positive when descending in the
       // direction of travel — front of the groove tilts down).
      const pitch = Math.atan2(-(b.y - a.y), length);

      // Spread grooves across the smooth track width.
      const grooveSpread = TRACK_HALF_WIDTH * 1.6;
      // Break each segment into short subpieces so a single line doesn't
      // visibly straddle two slope angles.
      const subPieces = Math.max(1, Math.round(length / 4));
      const subLen = length / subPieces;

      for (let g = 0; g < grooveCount; g++) {
        const lateral = -grooveSpread / 2 + grooveSpread * ((g + 0.5) / grooveCount);
        for (let s = 0; s < subPieces; s++) {
          const t = (s + 0.5) / subPieces;
          const cx = a.x + dx * t;
          const cz = a.z + dz * t;
          const gx = cx + right.x * lateral;
          const gz = cz + right.z * lateral;
          const groove = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.04, subLen * 0.96),
            grooveMat,
          );
          groove.position.set(gx, terrainY(gx, gz) + 0.03, gz);
          // YXZ order: yaw rotates the groove to align with the segment
          // direction in the XZ plane; then pitch tilts it forward to
          // hug the slope rather than floating above/below it.
          groove.rotation.order = 'YXZ';
          groove.rotation.y = yaw;
          groove.rotation.x = pitch;
          groove.receiveShadow = true;
          this.worldGroup.add(groove);
        }
      }
    }
  }

  // Jumps: wedge-shaped ramps placed at JUMP_ZS positions, oriented to
  // match the local tangent of the centerline.
  _buildJumps() {
    const rampMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.7, flatShading: true });
    const stripeMat = new THREE.MeshStandardMaterial({ color: 0xfbbf24, roughness: 0.7 });

    for (const z of JUMP_ZS) {
      const [cx] = sampleCenterline(z);
      const tangent = tangentAt(z);
      // After the geo transforms below, the ramp's local +Z is the back of
      // the wedge and -Z is the lip. We want the back to land on the uphill
      // side (i.e. -tangent), so the yaw rotating local +Z onto -tangent
      // is atan2(-tx, -tz). The previous atan2(tx, -tz) flipped the X
      // component for any segment where tangent.x ≠ 0 — visible as
      // sideways-facing ramps on Forest Pass and Mountain Plunge.
      const yaw = Math.atan2(-tangent.x, -tangent.z);
      const groundY = terrainY(cx, z);

      // Ramp shape: triangular profile, 8 long, 1.8 tall, 12 wide.
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.lineTo(8, 0);
      shape.lineTo(8, 1.8);
      shape.closePath();
      const geo = new THREE.ExtrudeGeometry(shape, { depth: 12, bevelEnabled: false });
      geo.translate(-8, 0, -6);
      geo.rotateY(Math.PI / 2);
      // Centre the wedge in its local Z so the visible ramp spans [-4, +4]
      // along the direction of travel, matching the footprint check below
      // (pad.halfLength === 4). The lip ends up at -Z, the back at +Z.
      geo.translate(0, 0, -4);

      const ramp = new THREE.Mesh(geo, rampMat);
      ramp.position.set(cx, groundY, z);
      ramp.rotation.y = yaw;
      ramp.castShadow = true;
      ramp.receiveShadow = true;
      this.worldGroup.add(ramp);

      // Hazard stripes on the lip.
      const stripeGeo = new THREE.BoxGeometry(12.2, 0.2, 0.8);
      const stripe = new THREE.Mesh(stripeGeo, stripeMat);
      stripe.position.set(cx, groundY + 1.9, z);
      stripe.rotation.y = yaw;
      this.worldGroup.add(stripe);

      // Footprint the GameLoop uses to detect "racer is on the ramp".
      // The ramp's wedge is 12 wide and 8 long, oriented along the segment.
      this.jumpPads.push({
        x: cx,
        z,
        yaw,
        halfLength: 4,
        halfWidth: 6,
      });
    }
  }

  // GameLoop uses this to detect ramp launches.
  getJumpPads() {
    return this.jumpPads;
  }

  // Race-end ceremony: places the top-3 racers on the podium boxes (with
  // their lane palettes) and gives each a gold/silver/bronze trophy. Called
  // by GameLoop when phase transitions to 'finished'. Safe to call repeatedly.
  showPodium(top3) {
    if (this._podiumGroup) {
      this.scene.remove(this._podiumGroup);
    }
    this._podiumGroup = new THREE.Group();
    // [{trophy, baseY, phase}] — populated for updatePodium() to animate.
    this._podiumTrophies = [];

    const trophyColors = [0xfde047, 0xcbd5e1, 0xb45309]; // gold, silver, bronze
    for (let i = 0; i < 3 && i < top3.length; i++) {
      const entry = top3[i];
      if (!entry) continue;
      const slot = this._podiumTops[i];
      if (!slot) continue;

      // Winner figure: same humanoid Racer using their lane palette,
      // positioned on top of the podium box facing the camera.
      const racer = new Racer(this._podiumGroup, entry.palette);
      racer.setPose({ x: slot.x, y: slot.y, z: slot.z }, Math.PI, 0);

      // Trophy: a chunky cup-on-pedestal raised up like the winner's
      // holding it overhead. Animated by updatePodium().
      const trophy = makeTrophy(trophyColors[i]);
      const baseY = slot.y + 1.8;
      trophy.position.set(slot.x, baseY, slot.z + 0.4);
      this._podiumGroup.add(trophy);
      this._podiumTrophies.push({
        trophy,
        baseX: slot.x,
        baseY,
        baseZ: slot.z + 0.4,
        phase: i * 0.7,
      });
    }
    this.scene.add(this._podiumGroup);
  }

  // Animates the podium trophies — sin-driven sideways wave, bobbing up
  // and down, tilting slightly so the winners read as celebrating.
  updatePodium(timeSec) {
    if (!this._podiumTrophies || this._podiumTrophies.length === 0) return;
    for (const item of this._podiumTrophies) {
      const t = timeSec * 2.6 + item.phase;
      const swing = Math.sin(t);
      const bob = Math.sin(t * 1.4 + 0.3);
      item.trophy.position.set(
        item.baseX + swing * 0.6,
        item.baseY + (Math.abs(bob) * 0.3),
        item.baseZ + bob * 0.15,
      );
      item.trophy.rotation.set(
        Math.sin(t * 1.2) * 0.18,
        swing * 0.7,
        Math.sin(t * 1.6) * 0.25,
      );
    }
  }

  // Camera focus point used during the race-over ceremony.
  getPodiumFocus() {
    return this._podiumFocus;
  }

  // Hides the podium figures (when returning to the lobby after a race).
  clearPodium() {
    if (this._podiumGroup) {
      this.scene.remove(this._podiumGroup);
      this._podiumGroup = null;
    }
    this._podiumTrophies = [];
  }

  _buildDecoration() {
    const { halfWidth, startZ, endZ, trackHalfWidth, transitionWidth } = this._terrainBounds;

    // Winter trees: a bare brown trunk with a few short branches and a snow
    // cap on top — closer to the leafless winter trees you see flanking the
    // course in Snowboard Kids than the green pines we had before.
    const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x5b3a1d, roughness: 0.95 });
    const snowMaterial = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.85 });
    const trunkGeo = new THREE.CylinderGeometry(0.18, 0.28, 3.6, 6);
    const branchGeo = new THREE.CylinderGeometry(0.05, 0.1, 1.0, 5);
    const snowCapGeo = new THREE.SphereGeometry(0.55, 8, 6);
    const snowCapTopGeo = new THREE.SphereGeometry(0.4, 8, 6);

    const rng = mulberry32(0xc0ffee);
    const treesPerSide = 80;
    for (let i = 0; i < treesPerSide * 2; i++) {
      const z = startZ - rng() * (startZ - endZ);
      const side = i < treesPerSide ? -1 : 1;
      const [cx] = sampleCenterline(z);
      const minOffset = trackHalfWidth + transitionWidth * 0.6;
      const maxOffset = halfWidth - 8;
      const lateral = minOffset + rng() * (maxOffset - minOffset);
      const x = cx + side * lateral;
      const y = terrainY(x, z);

      const tree = new THREE.Group();

      const trunk = new THREE.Mesh(trunkGeo, trunkMaterial);
      trunk.position.y = 1.8;
      trunk.castShadow = true;
      trunk.receiveShadow = true;
      tree.add(trunk);

      // A few short up-angled branches sprouting near the top of the trunk.
      const branchCount = 3 + Math.floor(rng() * 3);
      for (let bi = 0; bi < branchCount; bi++) {
        const branch = new THREE.Mesh(branchGeo, trunkMaterial);
        const angle = rng() * Math.PI * 2;
        const tilt = 0.5 + rng() * 0.6; // angled upward
        branch.position.set(Math.cos(angle) * 0.25, 2.6 + rng() * 0.6, Math.sin(angle) * 0.25);
        branch.rotation.set(tilt, angle, 0);
        branch.castShadow = true;
        tree.add(branch);
      }

      // Snow cap on top of the trunk.
      const cap = new THREE.Mesh(snowCapGeo, snowMaterial);
      cap.position.y = 3.6;
      cap.scale.y = 0.6;
      tree.add(cap);

      const capTop = new THREE.Mesh(snowCapTopGeo, snowMaterial);
      capTop.position.y = 3.95;
      capTop.scale.y = 0.5;
      tree.add(capTop);

      tree.position.set(x, y, z);
      const scale = 0.85 + rng() * 0.7;
      tree.scale.setScalar(scale);
      this.worldGroup.add(tree);
    }

    // Rocks: a few scattered icosahedra.
    const rockGeo = new THREE.IcosahedronGeometry(1.5, 0);
    const rockMat = new THREE.MeshStandardMaterial({ color: 0x6b7280, roughness: 0.8, flatShading: true });
    for (let i = 0; i < 30; i++) {
      const z = startZ - rng() * (startZ - endZ);
      const side = rng() > 0.5 ? -1 : 1;
      const [cx] = sampleCenterline(z);
      const lateral = trackHalfWidth + transitionWidth * 0.4 + rng() * 30;
      const x = cx + side * lateral;
      // Bury rocks slightly so they read as embedded in the snow.
      const y = terrainY(x, z) - 0.4;
      const rock = new THREE.Mesh(rockGeo, rockMat);
      rock.position.set(x, y, z);
      rock.rotation.set(rng() * Math.PI, rng() * Math.PI, rng() * Math.PI);
      const s = 0.6 + rng() * 1.4;
      rock.scale.setScalar(s);
      rock.castShadow = true;
      rock.receiveShadow = true;
      this.worldGroup.add(rock);
    }
  }

  getScene() {
    return this.scene;
  }
}

// --- helpers ---

// Linear interpolation along the centerline polyline based on Z.
// Returns [x, y] of the centerline at the given z.
function sampleCenterline(z) {
  const pts = TRACK_CENTERLINE;
  if (z >= pts[0].z) return [pts[0].x, pts[0].y];
  if (z <= pts[pts.length - 1].z) {
    const p = pts[pts.length - 1];
    return [p.x, p.y];
  }
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (z <= a.z && z >= b.z) {
      const t = (a.z - z) / (a.z - b.z);
      return [a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t];
    }
  }
  return [0, 0];
}

// Tangent direction (unit XZ) of the centerline at the given Z.
function tangentAt(z) {
  const pts = TRACK_CENTERLINE;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (z <= a.z && z >= b.z) {
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const m = Math.hypot(dx, dz);
      return { x: dx / m, z: dz / m };
    }
  }
  return { x: 0, z: -1 };
}

// Unit direction in the XZ plane from a to b.
function normaliseXZ(a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const m = Math.hypot(dx, dz);
  return { x: dx / m, z: dz / m };
}

// A small flag: short post with a triangular pennant on top.
function makeFlag(color) {
  const group = new THREE.Group();
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.06, 0.06, 3, 6),
    new THREE.MeshStandardMaterial({ color: 0xf1f5f9 }),
  );
  pole.position.y = 1.5;
  group.add(pole);

  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(1.2, 0.7),
    new THREE.MeshStandardMaterial({ color, side: THREE.DoubleSide }),
  );
  flag.position.set(0.6, 2.6, 0);
  group.add(flag);
  return group;
}

// Terrain surface height at world (x, z). Shared by the terrain mesh and
// every on-ground placement so nothing floats. The shape is: centerline Y
// in the smooth zone (lateral ≤ TRACK_HALF_WIDTH), plus an offset that ramps
// in via smoothstep across a 24-unit transition, scaled by ±28 from noise.
function terrainY(x, z) {
  const [centerX, centerY] = sampleCenterline(z);
  const lateral = Math.abs(x - centerX);
  const t = clamp((lateral - TRACK_HALF_WIDTH) / 24, 0, 1);
  const noiseAmp = smoothstep(t) * 28;
  return centerY + noise2(x, z) * noiseAmp;
}

// Cheap pseudo-noise: sum of sinusoids in two axes. Output roughly in [-1, 1].
function noise2(x, z) {
  return (
    Math.sin(x * 0.05) * Math.cos(z * 0.04) * 0.55 +
    Math.sin(x * 0.13 + 1.7) * Math.cos(z * 0.11 - 0.4) * 0.3 +
    Math.sin(x * 0.27 + 3.1) * Math.cos(z * 0.31 + 1.2) * 0.15
  );
}

function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }
function smoothstep(t) { return t * t * (3 - 2 * t); }

// Deterministic RNG so the same trees show up every reload.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A small trophy: dark pedestal under a coloured cup with two handles.
function makeTrophy(color) {
  const group = new THREE.Group();
  const baseMat = new THREE.MeshStandardMaterial({ color: 0x111827, roughness: 0.8 });
  const metal = new THREE.MeshStandardMaterial({ color, roughness: 0.4, metalness: 0.6 });

  const base = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.25, 0.8), baseMat);
  base.position.y = 0.125;
  base.castShadow = true;
  group.add(base);

  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.14, 0.5, 8), metal);
  stem.position.y = 0.45;
  stem.castShadow = true;
  group.add(stem);

  const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.18, 0.55, 12, 1, true), metal);
  cup.position.y = 1.0;
  cup.castShadow = true;
  group.add(cup);

  // Two side handles.
  const handleGeo = new THREE.TorusGeometry(0.16, 0.04, 6, 10, Math.PI);
  for (const side of [-1, 1]) {
    const handle = new THREE.Mesh(handleGeo, metal);
    handle.position.set(side * 0.32, 1.05, 0);
    handle.rotation.set(0, 0, side > 0 ? -Math.PI / 2 : Math.PI / 2);
    group.add(handle);
  }
  return group;
}

// A simple start/finish gate: two posts with a horizontal banner on top.
function buildGate(x, y, z, color, _label) {
  const group = new THREE.Group();
  const postGeo = new THREE.CylinderGeometry(0.35, 0.35, 7, 8);
  const postMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.6 });
  const bannerMat = new THREE.MeshStandardMaterial({ color });
  const bannerGeo = new THREE.BoxGeometry(14, 1.6, 0.3);

  const left = new THREE.Mesh(postGeo, postMat);
  left.position.set(-6, 3.5, 0);
  left.castShadow = true;
  group.add(left);

  const right = new THREE.Mesh(postGeo, postMat);
  right.position.set(6, 3.5, 0);
  right.castShadow = true;
  group.add(right);

  const banner = new THREE.Mesh(bannerGeo, bannerMat);
  banner.position.set(0, 7, 0);
  banner.castShadow = true;
  group.add(banner);

  // Base of the gate sits directly on the surface.
  group.position.set(x, y, z);
  return group;
}
